import React, { useState } from 'react';
import { Button, Menu, MenuItem, Box, Typography, Snackbar } from '@mui/material';
import ReplyIcon from '@mui/icons-material/Reply';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import RedditIcon from '@mui/icons-material/Reddit';
import XIcon from '@mui/icons-material/X';
import FacebookIcon from '@mui/icons-material/Facebook';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

// Build platform-specific reply text. Each platform has different length
// tolerance and formatting conventions, so each gets its own treatment.
// Every reply leads with the verdict, because 9 of the 34 statements are true or
// misleading, not false: "actually, ..." under a true claim made us look dumb.
// `answer` is the card's one-line punch (falls back to the snippet).
const buildReplies = (title, verdict, answer, shareLink) => {
  const claim = title.replace(/^"|"$/g, '').trim();
  const v = String(verdict || '').trim();
  const lead = v ? `${v.charAt(0).toUpperCase()}${v.slice(1).toLowerCase()}.` : 'Fact-checked:';

  // Reddit: markdown-friendly, longer is fine, inline link.
  const reddit = `> ${claim}\n\n**${lead}** ${answer}\n\n[The full fact-check, with sources](${shareLink})`;

  // X/Twitter: 280 char cap, tight. URL stays at the end. Truncate body if needed.
  const xUrlPart = ` ${shareLink}`;
  const xBudget = 280 - xUrlPart.length - 3; // 3 for "..."
  const xLead = `"${claim}": ${lead} ${answer}`;
  const xBody = xLead.length > xBudget ? `${xLead.slice(0, xBudget).trim()}...` : xLead;
  const twitter = `${xBody}${xUrlPart}`;

  // Facebook: prose paragraph, medium length, raw URL at end.
  const facebook = `Someone recently claimed "${claim}". ${lead} ${answer}\n\nThe full fact-check, with sources: ${shareLink}`;

  return { reddit, twitter, facebook };
};

export default function CopyReplyMenu({ title, verdict, answer, truthSnippet, shareLink }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const [toastMsg, setToastMsg] = useState('');
  const open = Boolean(anchorEl);

  const handleOpen = (e) => {
    setAnchorEl(e.currentTarget);
    if (window.gtag) window.gtag('event', 'copy_reply_open', { title });
  };
  const handleClose = () => setAnchorEl(null);

  const handleCopy = async (platform) => {
    const replies = buildReplies(title, verdict, answer || truthSnippet, shareLink);
    const text = replies[platform];
    try {
      await navigator.clipboard.writeText(text);
      setToastMsg(`Copied ${platform === 'twitter' ? 'X' : platform.charAt(0).toUpperCase() + platform.slice(1)} reply`);
      if (window.gtag) {
        window.gtag('event', 'copy_reply', { platform, title });
      }
    } catch (err) {
      console.error('Copy failed', err);
      setToastMsg('Copy failed: try again');
    }
    handleClose();
  };

  const items = [
    { key: 'reddit',   label: 'Reddit reply',    icon: <RedditIcon sx={{ fontSize: '1.1rem', color: '#FF4500' }} />,    hint: 'Markdown + sources' },
    { key: 'twitter',  label: 'X / Twitter',     icon: <XIcon sx={{ fontSize: '1rem', color: '#000' }} />,              hint: '280 chars, link at end' },
    { key: 'facebook', label: 'Facebook reply',  icon: <FacebookIcon sx={{ fontSize: '1.1rem', color: '#1877F2' }} />,  hint: 'Plain paragraph' },
  ];

  return (
    <>
      <Button
        variant="outlined"
        onClick={handleOpen}
        aria-haspopup="menu"
        aria-expanded={open}
        sx={{
          color: '#111111',
          borderColor: '#111111',
          borderRadius: 0,
          padding: '10px 18px',
          fontWeight: 600,
          fontSize: '0.875rem',
          textTransform: 'none',
          '&:hover': { backgroundColor: '#FFF6B8', borderColor: '#333333' },
        }}
      >
        <ReplyIcon sx={{ marginRight: '8px', fontSize: '1.2rem' }} />
        Reply to an idiot
        <ArrowDropDownIcon sx={{ ml: 0.5, fontSize: '1.2rem' }} />
      </Button>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        PaperProps={{
          sx: {
            borderRadius: 0,
            border: '2px solid #111111',
            boxShadow: '4px 4px 0 #111',
            mt: 0.5,
            minWidth: 240,
          },
        }}
      >
        <Box sx={{ px: 2, pt: 1.25, pb: 0.75 }}>
          <Typography sx={{ fontSize: '0.72rem', color: '#555555', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            Paste into an existing thread
          </Typography>
        </Box>
        {items.map(item => (
          <MenuItem key={item.key} onClick={() => handleCopy(item.key)} sx={{ py: 1.25, px: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, width: '100%' }}>
              <Box sx={{ width: 22, display: 'flex', justifyContent: 'center' }}>{item.icon}</Box>
              <Box>
                <Typography sx={{ fontSize: '0.9rem', fontWeight: 600, color: '#111111', lineHeight: 1.2 }}>
                  {item.label}
                </Typography>
                <Typography sx={{ fontSize: '0.72rem', color: '#555555', lineHeight: 1.2 }}>
                  {item.hint}
                </Typography>
              </Box>
            </Box>
          </MenuItem>
        ))}
      </Menu>

      <Snackbar
        open={!!toastMsg}
        autoHideDuration={1800}
        onClose={() => setToastMsg('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        message={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CheckCircleIcon sx={{ color: '#4CAF50', fontSize: '1.1rem' }} />
            <Typography sx={{ fontSize: '0.875rem' }}>{toastMsg}</Typography>
          </Box>
        }
        ContentProps={{
          sx: {
            backgroundColor: '#111111',
            color: '#fff',
            borderRadius: 0,
          },
        }}
      />
    </>
  );
}
